import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Col, Form, Row, Spin, Typography } from 'antd'
import { isEmpty } from 'lodash'
import PropTypes from 'prop-types'

import {
  DirtyPromptForm,
  FormButtons,
  FormTextareaInput,
  TermErrors,
} from '../../../common/components/sections'
import { PERMISSIONS } from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'
import { idPropType, treeOptionPropType } from '../../../common/prop-types'

import { INGREDIENT_PHRASE_FIELD_NAMES } from '../../constants'
import { useUpdateIngredientExtraDetailsMutation } from '../../hooks'
import {
  extraIngredientPropType,
  ingredientPhrasePropType,
} from '../../prop-types'
import { setIngredientPageIsDirty } from '../../redux/actions'
import { tokenizeTextByNewLine } from '../../utils'
import { IngredientPairingFormSection } from '../sections'

import { ingredientExtraDetailsFormInitialValues } from './initial-values'
import { validatePhrase } from './validators'

const IngredientExtraDetailsForm = ({
  ingredientId,
  phrases,
  extraIngredients,
  ingredientOptions,
  groupedFlatTreeDataByValue,
  flatTreeData,
}) => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.INGREDIENT.SAVE_EXTRA_INGREDIENTS,
  ])

  const [
    updateIngredientExtraDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateIngredientExtraDetailsMutation()

  const initialValues = useMemo(
    () =>
      ingredientExtraDetailsFormInitialValues({ phrases, extraIngredients }),
    [extraIngredients, phrases],
  )

  const negativeSynonymsRules = useMemo(
    () => [
      ({ getFieldValue }) => ({
        validator(_, value) {
          const negativeSynonymPhrases = tokenizeTextByNewLine(value)

          const pairingPhrases = tokenizeTextByNewLine(
            getFieldValue('pairingPhrases'),
          )

          const referencingPhrases = tokenizeTextByNewLine(
            getFieldValue('referencingPhrases'),
          )

          try {
            validatePhrase(
              negativeSynonymPhrases,
              [pairingPhrases, referencingPhrases],
              {
                placeholderExisting: false,
                conflicting: true,
                placeholderNumber: 1,
                incompletePlaceholder: true,
              },
            )

            return Promise.resolve()
          } catch (error) {
            return Promise.reject(error)
          }
        },
      }),
    ],
    [],
  )

  const handleSaveData = useCallback(
    data => updateIngredientExtraDetails(ingredientId, data),
    [ingredientId, updateIngredientExtraDetails],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Ingredient details error',
        description: saveError?.message,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
  }, [extraIngredients, phrases, resetForm])

  useEffect(() => {
    dispatch(setIngredientPageIsDirty({ isExtraIngredientsDirty: isDirty }))
  }, [dispatch, isDirty])

  const { errorDetails: duplicated } = saveError || {}

  return isSaveLoading ? (
    <Spin />
  ) : (
    <DirtyPromptForm
      isDirty={isDirty}
      form={form}
      initialValues={initialValues}
      name="extraForm"
      onFinish={handleSaveData}
      autoComplete="off"
      onFieldsChange={setToDirty}
    >
      <Typography.Title level={3}>
        Phrases & Phrase Ingredients
      </Typography.Title>
      {!isEmpty(duplicated) && <TermErrors errors={duplicated} />}
      <Row gutter={[16]}>
        <Col span={12}>
          <FormTextareaInput
            disabled={!isFormEnabled}
            name={INGREDIENT_PHRASE_FIELD_NAMES.NEGATIVE}
            label="Negative Synonyms"
            labelAbove
            autoSize
            placeholder="Input negative synonyms..."
            rules={negativeSynonymsRules}
          />
        </Col>
      </Row>
      <IngredientPairingFormSection
        ingredientId={ingredientId}
        disabled={!isFormEnabled}
        form={form}
        ingredientOptions={ingredientOptions}
        textareaName={INGREDIENT_PHRASE_FIELD_NAMES.PAIRING}
        textareaLabel="Pairing Phrases (new line separated)"
        textareaPlaceholder="Input pairing phrases..."
        ingredientListName="pairingIngredientIds"
        ingredientListLabel="Pairing Ingredients"
        ingredientListPlaceholder="Select a new pairing ingredient..."
        isDirty={isDirty}
        groupedFlatTreeDataByValue={groupedFlatTreeDataByValue}
        flatTreeData={flatTreeData}
        initialValue={initialValues.pairingIngredientIds}
      />
      <IngredientPairingFormSection
        ingredientId={ingredientId}
        disabled={!isFormEnabled}
        form={form}
        ingredientOptions={ingredientOptions}
        textareaName={INGREDIENT_PHRASE_FIELD_NAMES.REFERENCING}
        textareaLabel="Referencing Phrases (new line separated)"
        textareaPlaceholder="Input reference phrases..."
        ingredientListName="referenceIngredientIds"
        ingredientListLabel="Reference Ingredients"
        ingredientListPlaceholder="Select a new reference ingredient..."
        isDirty={isDirty}
        groupedFlatTreeDataByValue={groupedFlatTreeDataByValue}
        flatTreeData={flatTreeData}
        initialValue={initialValues.referenceIngredientIds}
      />
      <FormButtons
        permissions={[PERMISSIONS.INGREDIENT.SAVE_EXTRA_INGREDIENTS]}
        isDisabled={!isDirty}
        onReset={resetForm}
      />
    </DirtyPromptForm>
  )
}

IngredientExtraDetailsForm.propTypes = {
  ingredientId: idPropType.isRequired,
  extraIngredients: PropTypes.arrayOf(extraIngredientPropType),
  phrases: PropTypes.arrayOf(ingredientPhrasePropType),
  ingredientOptions: PropTypes.arrayOf(treeOptionPropType).isRequired,
  groupedFlatTreeDataByValue: PropTypes.object.isRequired,
  flatTreeData: PropTypes.arrayOf(PropTypes.object).isRequired,
}

IngredientExtraDetailsForm.defaultProps = {
  extraIngredients: [],
  phrases: [],
}

export default IngredientExtraDetailsForm
